import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import type { TFunction } from 'i18next'

import { ChevronDownIcon, FolderIcon, TemporaryChatIcon } from '../components/icons'
import { HedefLogo } from '../components/HedefLogo'
import { StreamingMessage } from '../components/StreamingMessage'
import { ApiError } from '../lib/api'
import { dayKey, formatDayLabel } from '../lib/datetime'
import { describeApiError, translateCode } from '../lib/errors'
import {
  getMessages,
  closeTemporaryConversation,
  listProjects,
  notifyProjectsChanged,
  notifyTemporaryChanged,
  continueAnswer,
  regenerate,
  submitMessage,
  switchVariant,
  type Conversation,
  type Message,
} from '../lib/conversations'
import { randomId } from '../lib/uuid'
import { useSession } from '../session/SessionProvider'
import { useStreams } from '../stream/StreamManager'
import { GalleryProvider, galleryFilesFromMessages } from '../attachments/gallery'
import { Composer, type ImageOptions } from './Composer'
import { HistoryMessage } from './HistoryMessage'

/**
 * Bir konusmanin gorunumu. Projenin asil amaci burada iniyor.
 *
 * ## Akan mesaj ile gecmis mesaj ARASINDAKI ayrim
 *
 * Sunucu `messages.content`i yalnizca TAMAMLANINCA yaziyor (uretim sirasinda
 * kismi metin `run_chunks`ta). Yani `status: 'streaming'` bir asistan satiri
 * BOS geliyor ve icerigi akistan doluyor. Bunun sonucu cok onemli: yeniden
 * baglanmada `after=0` "simdiye kadar olan her sey" oldugu icin metin
 * cift GORUNMUYOR - tek kaynak var.
 *
 * Istisna `interrupted`: boot recovery kismi metni `messages.content`e
 * YAZIYOR, cunku o run bir daha hic akmayacak ve kismi metni atmak
 * kullanicinin okuyabilecegi bir yanitin yarisini silmek olurdu.
 *
 * ## Kaydirma
 *
 * `useLayoutEffect` + "dipteydi mi" kontrolu: kullanici yukari kaydirip eski
 * bir mesaji okuyorken yeni token gelince EKRAN ZIPLAMAMALI. Kosulsuz
 * `scrollTo` bu hatayi uretiyor ve okuma sirasinda cildirtici oluyor.
 */
export function ConversationView() {
  const { t } = useTranslation()
  const { conversationId = null } = useParams()
  const navigate = useNavigate()
  // Kenar cubugunda "Bu projede yeni sohbet": `/?project=<id>`. Ilk mesaj
  // sohbeti bu projenin altinda aciyor.
  const [searchParams] = useSearchParams()
  const projectId = conversationId === null ? searchParams.get('project') : null
  const projectName = useProjectName(projectId)
  // Kenar cubugundaki/besteci uzerindeki "Geçici sohbet": `/?temporary=1`.
  // Ilk mesaj sohbeti GECICI aciyor. Sohbet olustuktan sonra bilgi URL'de
  // DEGIL sunucudan geliyor (`conversation.temporary`) - yenilemeden sonra
  // da dogru olmasi gereken bir sey.
  const startTemporary = conversationId === null && searchParams.get('temporary') === '1'
  const { runs, attach, release, cancel, restored, runFor, runForMessage } = useStreams()
  const { state: session } = useSession()
  const avatarId = session?.user?.avatar_attachment_id

  const [conversation, setConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])

  /**
   * Oyu yerel listeye yaziyor.
   *
   * Gecmisi yeniden CEKMIYORUZ: tek bir oy icin 50 mesaji tekrar indirmek
   * gereksiz, ve daha kotusu kaydirma konumunu bozuyor. Sunucudaki gercek
   * durum `Feedback` bileseninin kendi cagrisiyla zaten guncellendi; burada
   * yalnizca gorunum ayni gercege getiriliyor.
   */
  const noteFeedback = useCallback((messageId: string, next: Message['feedback']) => {
    setMessages((current) =>
      current.map((item) => (item.id === messageId ? { ...item, feedback: next } : item)),
    )
  }, [])
  const [loading, setLoading] = useState(conversationId !== null)
  const [error, setError] = useState<string | null>(null)
  const thread = useRef<HTMLDivElement>(null)
  const wasAtBottom = useRef(true)

  // `t`yi REF uzerinden okuyoruz. Dogrudan closure'a almak, dil degistiginde
  // `t`nin kimligi degistigi icin `reload`u yeniden yaratir ve bagli
  // efektler butun gecmisi GEREKSIZ YERE tekrar cekerdi. Ref, "her zaman en
  // guncel deger, ama bagimlilik degil" icin dogru arac.
  const tRef = useRef(t)
  tRef.current = t

  const activeRun = conversationId ? runFor(conversationId) : undefined

  // --- gecmisi yukle -------------------------------------------------------

  /**
   * Gecmisi yukler.
   *
   * `background: true` iken "Yukleniyor…" GOSTERILMIYOR. Bu onemli: run
   * bittiginde ve varyant degistirildiginde gecmisi tazeliyoruz ve o an
   * spinner gostermek, kullanicinin okudugu tamamlanmis yanitin bir anlik
   * KAYBOLMASI demek olurdu - yani gorunur bir yanip sonme.
   */
  const reload = useCallback(async (id: string, options: { background?: boolean } = {}) => {
    if (!options.background) setLoading(true)
    setError(null)
    try {
      const data = await getMessages(id)
      setConversation(data.conversation)
      setMessages(data.items)
    } catch (cause) {
      // 404 = yok VEYA baskasina ait. Ikisini ayirt ETMIYORUZ; sunucu da
      // ayirt etmiyor (403 varligi dogrular ve bu bir sizinti).
      setError(
        tRef.current(
          cause instanceof ApiError && cause.status === 404
            ? 'chat.error.notFound'
            : 'chat.error.loadFailed',
        ),
      )
      setMessages([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (conversationId === null) {
      setConversation(null)
      setMessages([])
      setLoading(false)
      setError(null)
      return
    }
    void reload(conversationId)
  }, [conversationId, reload])

  // Run terminal olunca gecmisi bir kez tazele: kalici `messages` satiri
  // artik stats, model ve nihai durumu tasiyor.
  //
  // `runs`in kendisi degil IMZASI izleniyor (id + durum): dizi her
  // re-render'da yeni referans olabiliyor ve o zaman bu efekt sonsuz
  // yeniden yukleme dongusune girerdi.
  const runSignature = runs.map((run) => `${run.id}:${run.status}`).join(',')
  const previousSignature = useRef(runSignature)
  useEffect(() => {
    const changed = previousSignature.current !== runSignature
    previousSignature.current = runSignature
    if (changed && conversationId) void reload(conversationId, { background: true })
  }, [runSignature, conversationId, reload])

  // Kalici mesaj artik `streaming` DEGIL: run'i birakabiliriz. Devir teslim
  // bu sirayla kayipsiz - akan metin, kalici metin gelene KADAR ekranda
  // kaliyor.
  useEffect(() => {
    for (const message of messages) {
      if (message.role !== 'assistant' || message.status === 'streaming') continue
      const run = runForMessage(message.id)
      if (run) release(run.id)
    }
  }, [messages, runForMessage, release])

  // --- kaydirma ------------------------------------------------------------
  //
  // `wasAtBottom` (ref) mesaj listesi BUYUMEDEN once "dipteydim" bilgisini
  // tasiyor - bu, yeni icerik geldiginde otomatik takip edip etmeme
  // kararinda kullaniliyor. `atBottom` (state) ise SADECE "en alta don"
  // butonunun gorunurlugu icin - ref state degisikligi tetiklemez.
  const [atBottom, setAtBottom] = useState(true)
  // Bir konusma DEGISTIGINDE/yeniden yuklendiginde "en alta zorla" istegi -
  // `wasAtBottom`tan AYRI bir ref, cunku asagidaki olcum efekti onu
  // KORUSUZCA EZEBILIYOR (bkz. asagidaki not). Tuketilince (kaydirma
  // gerceklestiginde) `false`e donuyor - tek seferlik.
  const forceScrollNext = useRef(true)

  const isNearBottom = (node: HTMLDivElement) =>
    // 40px tolerans: tam dip nadiren tam sayi oluyor (alt piksel).
    node.scrollHeight - node.scrollTop - node.clientHeight < 40

  // Render'dan ONCE dipte miydik? Sonrasinda olcmek cok gec olur - mesaj
  // listesi bu efektten SONRA buyuyor ve scrollHeight degisiyor.
  //
  // ## GERCEK bir hatanin duzeltmesi: bu olcum konusma DEGISTIRME/YENIDEN
  // YUKLEME'de GUVENILMEZ
  //
  // Bu efekt her render SONRASI (deps YOK) kosuyor ve `thread.current`i O
  // ANDAKI haliyle olcuyor. Bir konusma degistiginde/sayfa yenilendiginde,
  // `messages` ESKI konusmanin (veya bos) listesinden YENI konusmanin TAM
  // gecmisine BIR SICRAMADA gecer - ve bu SICRAMA, mesajlarin gercekten
  // GELDIGI render'da (conversationId degisiminden AYRI, ASENKRON fetch
  // cozuldugunde) gerceklesiyor. O render'in commit'inden SONRA bu efekt
  // kosarken DOM ZATEN yeni (uzun) icerigi yansitiyor, ama `scrollTop`
  // hala ESKI konumunda (ornegin ESKI konusmanin kaydirma konumu) - yani
  // "yeni yuksek scrollHeight - eski kucuk scrollTop" olcumu YANLIŞLIKLA
  // "dipte degiliz" sonucuna varip `wasAtBottom.current`i `false` yapiyor,
  // TAM DA "conversationId degisince en alta zorla" efektinin (asagida)
  // dogru sekilde `true` yaptigi degeri EZEREK. `forceScrollNext` bu
  // yuzden bu efektten TAMAMEN AYRI tutuluyor - burada HIC dokunulmuyor.
  useLayoutEffect(() => {
    const node = thread.current
    if (!node) return
    wasAtBottom.current = isNearBottom(node)
  })

  // Kullanicinin KENDI kaydirmasini izler (fare tekerlegi, dokunma,
  // programatik `scrollTop` atamalari da dahil - hepsi `scroll` olayi
  // uretir). Yukaridaki efektten AYRI: o mesaj EKLENMEDEN once olcuyor, bu
  // ise kullanici herhangi bir an nerede oldugunu SURDURUYOR - butonun
  // gorunmesi/kaybolmasi ve "geri dipe donunce takip devam etsin" icin.
  useEffect(() => {
    const node = thread.current
    if (!node) return
    const onScroll = () => {
      const near = isNearBottom(node)
      wasAtBottom.current = near
      setAtBottom(near)
    }
    node.addEventListener('scroll', onScroll, { passive: true })
    return () => node.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const node = thread.current
    if (!node) return
    if (wasAtBottom.current || forceScrollNext.current) {
      node.scrollTop = node.scrollHeight
      // Tek seferlik: tuketildi. Bundan sonraki buyumeler normal
      // "dipteyse takip et" davranisina (`wasAtBottom`) donuyor.
      forceScrollNext.current = false
    }
  }, [messages, activeRun?.id])

  // Yeni bir sohbet acildiginda (farkli bir konusmaya gecildiginde) ONCEKI
  // sohbetten kalan "yukarida kalmisti" durumu SIZMAMALI - her sohbet HER
  // ZAMAN en alttan acilmali. `forceScrollNext` burada set ediliyor - yukaridaki
  // olcum efekti bunu EZEMEZ, cunku o efekt SADECE `wasAtBottom`a yaziyor.
  useEffect(() => {
    wasAtBottom.current = true
    forceScrollNext.current = true
    setAtBottom(true)
  }, [conversationId])

  // Akan yanit sirasinda her token'da cagriliyor (bkz. `StreamingMessage`e
  // `onGrow` olarak gecirilmesi) - o bilesen KENDI basina re-render oluyor
  // (store'a dogrudan abone), yani buradaki `[messages, activeRun?.id]`
  // efekti token basina TETIKLENMIYOR. Kullanici dipteyse takip burada.
  const followIfAtBottom = useCallback(() => {
    const node = thread.current
    if (!node || !wasAtBottom.current) return
    node.scrollTop = node.scrollHeight
  }, [])

  const scrollToBottom = useCallback(() => {
    const node = thread.current
    if (!node) return
    node.scrollTop = node.scrollHeight
    wasAtBottom.current = true
    setAtBottom(true)
  }, [])

  // --- gonderme ------------------------------------------------------------

  const send = useCallback(
    async (
      content: string,
      attachmentIds: string[],
      model: string | null,
      imageOptions?: ImageOptions,
    ) => {
      setError(null)
      try {
        const result = await submitMessage({
          conversationId,
          projectId,
          content,
          // Yalnizca ID'ler gidiyor - istekte HICBIR YERDE base64 yok.
          // Onceki halde 3 resimli bir konusma her turda ~4 MB yeniden
          // yukleniyordu.
          attachmentIds,
          // Idempotency: zayif baglantida retry turu IKIYE KATLAMASIN.
          // `crypto.randomUUID()` DEGIL: duz HTTP'de (localhost disi) tanimsiz
          // ve cagirmak gondermeyi tamamen kiriyordu - bkz. `lib/uuid.ts`.
          clientMessageId: randomId(),
          model,
          imageSize: imageOptions?.size ?? null,
          imageQuality: imageOptions?.quality ?? null,
          temporary: startTemporary,
        })

        attach({
          id: result.run.id,
          conversation_id: result.conversation.id,
          assistant_message_id: result.assistant_message.id,
          status: 'queued',
          model: null,
          think: result.assistant_message.think,
          chunk_count: 0,
          cancel_requested: false,
          error_code: null,
          queue_position: result.run.queue_position,
        })

        if (conversationId === null) {
          // Yeni sohbet: URL'e gec. `replace` DEGIL - kullanici geri
          // tusuyla bos ekrana donebilmeli.
          navigate(`/c/${result.conversation.id}`)
          if (result.conversation.project_id) notifyProjectsChanged()
          // Gecici sohbet SUNUCUDA simdi olustu: kenar cubugundaki satir
          // ancak bu haberle doluyor.
          if (result.conversation.temporary) notifyTemporaryChanged()
        }
        setConversation(result.conversation)
        setMessages((current) => [...current, result.user_message, result.assistant_message])
        wasAtBottom.current = true
      } catch (cause) {
        setError(describeSubmit(cause, tRef.current))
      }
    },
    [attach, conversationId, navigate, projectId, startTemporary],
  )

  /** Ust seritteki "Kapat": icerik sunucuda siliniyor, geri donusu yok. */
  const closeTemporary = useCallback(async () => {
    if (!conversationId) return
    try {
      await closeTemporaryConversation(conversationId)
      notifyTemporaryChanged()
      navigate('/')
    } catch (cause) {
      setError(describeSubmit(cause, tRef.current))
    }
  }, [conversationId, navigate])

  const retry = useCallback(
    async (userMessageId: string) => {
      if (!conversationId) return
      setError(null)
      try {
        const result = await regenerate(conversationId, userMessageId)
        attach({
          id: result.run.id,
          conversation_id: conversationId,
          assistant_message_id: result.assistant_message_id,
          status: 'queued',
          model: null,
          think: 'auto',
          chunk_count: 0,
          cancel_requested: false,
          error_code: null,
          queue_position: result.run.queue_position,
        })
        await reload(conversationId, { background: true })
      } catch (cause) {
        setError(describeSubmit(cause, tRef.current))
      }
    },
    [attach, conversationId, reload],
  )

  /**
   * "Devam et": yarida kesilmis yaniti KALDIGI YERDEN surdurur.
   *
   * `retry`den farki yeni bir dal acmamasi - sunucu AYNI mesaji yeniden acip
   * kismi metni akisa geri veriyor, yani buradan bakinca akis normal bir
   * uretimden farksiz.
   */
  const resume = useCallback(
    async (assistantMessageId: string) => {
      if (!conversationId) return
      setError(null)
      try {
        const result = await continueAnswer(conversationId, assistantMessageId)
        attach({
          id: result.run.id,
          conversation_id: conversationId,
          assistant_message_id: result.assistant_message.id,
          status: 'queued',
          model: null,
          think: 'auto',
          chunk_count: 0,
          cancel_requested: false,
          error_code: null,
          queue_position: result.run.queue_position,
        })
        await reload(conversationId, { background: true })
      } catch (cause) {
        setError(describeSubmit(cause, tRef.current))
      }
    },
    [attach, conversationId, reload],
  )

  /**
   * Varyant dallari arasinda gezinme.
   *
   * Hedef kardesi SUNUCU buluyor: yalnizca "hangi mesaj" ve "hangi yon"
   * gonderiliyor. Istemcide dal secmek, agaci istemcide yeniden yorumlamak
   * demek olurdu - ve terk edilmis dallar baglama sizarsa model tutarsiz
   * davraniyor. Agac TEK yerde yasiyor.
   */
  const switchTo = useCallback(
    async (messageId: string, delta: number) => {
      if (!conversationId) return
      setError(null)
      try {
        await switchVariant(conversationId, messageId, delta > 0 ? 1 : -1)
        await reload(conversationId, { background: true })
      } catch (cause) {
        // 409 = bu yonde baska yanit yok. Butonlar zaten pasif oldugu icin
        // normalde ulasilamaz; yarista olursa sessizce gecmiyoruz.
        setError(
          cause instanceof ApiError
            ? (translateCode(cause, tRef.current) ?? cause.message)
            : tRef.current('chat.error.variantFailed'),
        )
      }
    },
    [conversationId, reload],
  )

  // --- render --------------------------------------------------------------

  // Onizleme galerisi: konusmadaki TUM ekler (kullanici yuklemeleri + arac
  // ciktilari) mesaj sirasiyla; modal bunlar arasinda gezinebiliyor.
  const galleryFiles = useMemo(() => galleryFilesFromMessages(messages), [messages])

  return (
    <GalleryProvider files={galleryFiles}>
      <div className="chat">
        {/*
          Gecici sohbet seridi. Sohbet ACILDIGINDA da gorunmesi sart:
          kullanici baglantiyla ya da yenilemeden sonra girdiginde bu
          konusmanin kaydedilmedigini bilmeli. Kapatma dugmesi burada cunku
          icerigin silinmesinin TEK yolu bu (otomatik silme yok).
        */}
        {(conversation?.temporary || startTemporary) && (
          <div className="temporary-banner" role="status">
            <TemporaryChatIcon size={15} />
            <span>
              {conversation?.temporary
                ? t('chat.temporary.active')
                : t('chat.temporary.willStart')}
            </span>
            {conversation?.temporary && (
              <button type="button" className="temporary-banner-close" onClick={closeTemporary}>
                {t('chat.temporary.close')}
              </button>
            )}
          </div>
        )}
        <div className="thread-viewport">
          <div className="thread" ref={thread}>
            {loading && <p className="sidebar-note">{t('common.loading')}</p>}
            {error && !loading && (
              <p className="msg-error" role="alert">
                {error}
              </p>
            )}
            {!loading && !error && messages.length === 0 && projectName && (
              <p className="project-intro">
                <FolderIcon size={15} />
                {t('chat.sidebar.newChatInProjectNamed', { name: projectName })}
              </p>
            )}
            {!loading && !error && messages.length === 0 && <Intro />}

            {/*
          Gun ayraci: WhatsApp'taki gibi - "Bugun"/"Dun"/tam tarih - mesaj
          listesindeki gun degistiginde araya giriyor. `position: sticky`
          (CSS) sayesinde o gunun mesajlari kaydirilirken ustte YAPISIK
          kaliyor.

          ## Neden gun basina bir SARMALAYICI (`.date-group`) sart

          Onceki halde ayrac ve mesajlar `.thread` icinde DUZ kardesti ve
          `position: sticky` o KAPSAYICI kutunun (`.thread`) TAMAMINA gore
          calisiyordu - yani bir ayrac, kendi gunu ekrandan cikip SONRAKI
          ayrac gelince degil, ancak `.thread`in KENDI SONUNA kadar yapisik
          kaliyordu. Sonraki ayrac da sticky oldugunda ikisi AYNI pikselde
          ust uste biniyordu (DOM'da dogrulandi - ikisinin de
          `getBoundingClientRect()`i BIREBIR ayniydi) ve `--glass-bg` YARI
          SAYDAM oldugu icin alttaki eski etiket bulaniklasarak GORUNMEYE
          devam ediyordu ("12 Ağustos" ile "Dün" ust uste yazilmis gibi
          gorunmesinin sebebi buydu).

          Dogru duzeltme: her gunun ayraci + o gune ait mesajlari TEK bir
          `position: relative` sarmalayiciya (`.date-group`) koymak. Sticky
          bir elemanin yapisma araligi EN YAKIN atasinin kutusuyla sinirli -
          sarmalayici sayesinde ayrac yalnizca KENDI grubu ekranda oldugu
          surece yapisik kalip, grup kaydirilinca DOGAL olarak onunla
          birlikte ekrandan cikiyor; ekstra JS/scroll dinleyicisi hala
          gerekmiyor.
        */}
            {(() => {
              // Yanit suresi KULLANICININ mesajindan sayiliyor: kuyrukta
              // beklenen sure de kullanicinin bekledigi suredir. Asistan
              // mesajinin `parent_message_id`si o kullanici mesajini gosteriyor.
              const byId = new Map(messages.map((item) => [item.id, item]))
              const askedAt = (message: (typeof messages)[number]) =>
                message.parent_message_id
                  ? (byId.get(message.parent_message_id)?.created_at ?? null)
                  : null

              const groups: { key: string; messages: typeof messages }[] = []
              let previousDateKey: string | null = null
              for (const message of messages) {
                const currentDateKey = dayKey(message.created_at)
                if (previousDateKey === currentDateKey) {
                  groups[groups.length - 1].messages.push(message)
                } else {
                  groups.push({ key: message.id, messages: [message] })
                }
                previousDateKey = currentDateKey
              }

              return groups.map((group) => (
                <div className="date-group" key={group.key}>
                  <div className="date-divider" role="separator">
                    <span>{formatDayLabel(group.messages[0].created_at, t)}</span>
                  </div>
                  {group.messages.map((message) => {
                    const run = runForMessage(message.id)

                    // Run varsa - terminal olsa BILE - metin store'da ve
                    // ekranda. Terminal run yalnizca kalici mesaj geldiginde
                    // birakiliyor (yukaridaki efekt), yani devir teslim
                    // sirasinda hicbir an bos veya yanlis icerik gorunmuyor.
                    if (run) {
                      return (
                        <StreamingMessage
                          key={message.id}
                          runId={run.id}
                          onGrow={followIfAtBottom}
                        />
                      )
                    }
                    if (message.status === 'streaming' && restored) {
                      // Aktif run YOK ama satir hala `streaming`: is baska bir
                      // surecte olmus ve boot recovery henuz kosmamis
                      // olabilir. Kismi metni gosteriyoruz ve durumu
                      // SOYLUYORUZ - sessizce bos bir balon birakmak
                      // uygulamanin donmus gibi gorunmesine yol acar.
                      return (
                        <HistoryMessage
                          key={message.id}
                          message={message}
                          avatarId={avatarId}
                          askedAt={askedAt(message)}
                          foreignAttachments={Boolean(conversation?.copied_from_id)}
                        />
                      )
                    }
                    return (
                      <HistoryMessage
                        key={message.id}
                        message={message}
                        avatarId={avatarId}
                        // Toplam sure KULLANICININ mesajindan sayiliyor:
                        // kuyrukta beklenen sure de kullanicinin bekledigi
                        // suredir (bkz. `HistoryMessage`).
                        askedAt={askedAt(message)}
                        // Kopyalanmis/catallanmis konusmanin ekleri baska bir
                        // kullanicidan geliyor: HTML onizlemesi once uyarir.
                        foreignAttachments={Boolean(conversation?.copied_from_id)}
                        onRegenerate={retry}
                        onContinue={message.role === 'assistant' ? resume : undefined}
                        onSwitchVariant={message.role === 'assistant' ? switchTo : undefined}
                        onFeedback={message.role === 'assistant' ? noteFeedback : undefined}
                      />
                    )
                  })}
                </div>
              ))
            })()}
          </div>
          {!atBottom && (
            <button
              type="button"
              className="scroll-bottom-btn"
              onClick={scrollToBottom}
              aria-label={t('chat.scrollToBottom')}
            >
              <ChevronDownIcon size={20} />
            </button>
          )}
        </div>

        <Composer
          conversationId={conversationId}
          busy={Boolean(activeRun)}
          onSend={send}
          onStop={activeRun ? () => void cancel(activeRun.id) : undefined}
          showSuggestions={!loading && !error && messages.length === 0}
          temporary={startTemporary}
          // Yalnizca YENI sohbette: anahtar `/` ile `/?temporary=1` arasinda
          // gidip geliyor. Var olan bir sohbette anahtar HIC gorunmuyor -
          // gosterip pasif birakmak "neden kapatamiyorum" sorusunu dogururdu.
          onToggleTemporary={
            conversationId === null
              ? () => navigate(startTemporary ? '/' : '/?temporary=1')
              : undefined
          }
        />
        {conversation?.copied_from_id && <p className="sidebar-note">{t('chat.copiedNotice')}</p>}
      </div>
    </GalleryProvider>
  )
}

/** `?project=` ile acilan bos sohbette gosterilecek proje adi. */
function useProjectName(projectId: string | null): string | null {
  const [name, setName] = useState<string | null>(null)
  useEffect(() => {
    setName(null)
    if (!projectId) return
    let cancelled = false
    listProjects()
      .then((page) => {
        const found = page?.items?.find((item) => item.id === projectId)
        if (!cancelled) setName(found?.name ?? null)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [projectId])
  return name
}

function Intro() {
  const { t } = useTranslation()

  const features = [
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: t('auth.shell.point.local'),
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: t('auth.shell.point.background'),
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: t('auth.shell.point.persistent'),
    },
  ]

  return (
    <div className="intro intro-home">
      {/* Başlık alanı */}
      <div className="intro-hero">
        <div className="intro-logo" aria-hidden="true">
          <HedefLogo height={40} />
        </div>
        <h1 className="intro-title">{t('chat.intro.title')}</h1>
        <p className="intro-body">{t('chat.intro.body')}</p>
      </div>

      {/* Özellik kartları */}
      <ul className="intro-features" aria-label={t('chat.intro.featuresLabel')}>
        {features.map((f, i) => (
          <li key={i} className="intro-feature" style={{ animationDelay: `${80 + i * 60}ms` }}>
            <span className="intro-feature-icon">{f.icon}</span>
            <span className="intro-feature-text">{f.title}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Gonderme hatalarini kullaniciya cevirir.
 *
 * Kuyruk ve kota redleri BEKLENEN durumlar, ariza degil: tek GPU'da bekleme
 * normal. Genel bir "hata olustu" mesaji kullaniciyi ne yapacagini bilmeden
 * birakirdi.
 */
function describeSubmit(cause: unknown, t: TFunction): string {
  if (!(cause instanceof ApiError)) return t('chat.error.sendFailed')
  if (cause.status === 503) return t('chat.error.serverBusy')
  if (cause.status === 409) return t('chat.error.runInProgress')
  if (cause.status === 429 && cause.retryAfter) {
    return t('chat.error.retryAfter', {
      message: translateCode(cause, t) ?? cause.message,
      seconds: cause.retryAfter,
    })
  }
  return describeApiError(cause, t)
}
